import React from 'react';
import { Phone, Mail, MapPin, MessageSquare, Award, Lightbulb } from 'lucide-react';
import { contactInfo } from '../mock/mockData';

const ContactSection = () => {

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-amber-50 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Meet Our <span className="text-amber-700">Team</span>
            </h2>
            <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Excellence backed by decades of expertise and unwavering commitment to quality
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-amber-200 h-full flex flex-col">
                {/* Name Header */}
                <div className="text-center mb-6 pb-6 border-b-2 border-amber-200">
                  <h3 className="text-3xl font-bold text-gray-800">Hasmukh Patel</h3>
                  <p className="text-amber-700 font-medium mt-1">Contact Information</p>
                </div>
                
                <div className="space-y-6 flex-grow">
                  {/* Phone */}
                  <div className="flex items-start space-x-4 p-4 bg-amber-50 rounded-xl hover:shadow-md transition-shadow border border-amber-100">
                    <div className="bg-amber-600 p-3 rounded-lg">
                      <Phone className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 mb-1">Phone</h4>
                      <a 
                        href={`tel:${contactInfo.phone}`}
                        className="text-amber-700 hover:text-amber-800 font-medium"
                      >
                        {contactInfo.phone}
                      </a>
                    </div>
                  </div>

                  {/* WhatsApp */}
                  <div className="flex items-start space-x-4 p-4 bg-amber-50 rounded-xl hover:shadow-md transition-shadow border border-amber-100">
                    <div className="bg-green-600 p-3 rounded-lg">
                      <MessageSquare className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 mb-1">WhatsApp</h4>
                      <a 
                        href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-700 hover:text-green-800 font-medium"
                      >
                        {contactInfo.whatsapp}
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start space-x-4 p-4 bg-amber-50 rounded-xl hover:shadow-md transition-shadow border border-amber-100">
                    <div className="bg-amber-600 p-3 rounded-lg">
                      <Mail className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 mb-1">Email</h4>
                      <a 
                        href={`mailto:${contactInfo.email}`}
                        className="text-amber-700 hover:text-amber-800 font-medium break-all"
                      >
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-start space-x-4 p-4 bg-amber-50 rounded-xl hover:shadow-md transition-shadow border border-amber-100">
                    <div className="bg-amber-600 p-3 rounded-lg">
                      <MapPin className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 mb-1">Address</h4>
                      <p className="text-gray-700 leading-relaxed">
                        {contactInfo.address}
                      </p>
                    </div>
                  </div>

                  {/* Google Map */}
                  <div className="rounded-xl overflow-hidden shadow-md border border-amber-200">
                    <iframe
                      src="https://maps.google.com/maps?q=14,+Dhara+Estate,+Santej-Khatraj+Road,+Santej,+Kalol,+Gandhinagar,+Gujarat,+India&t=&z=15&ie=UTF8&iwloc=&output=embed"
                      width="100%"
                      height="200"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Camel Rubber Industries Location"
                    ></iframe>
                  </div>
                  
                  {/* View on Google Maps Button */}
                  <button
                    type="button"
                    onClick={() => {
                      const url = 'https://www.google.com/maps/place/Camel+Rubber+Industries/@23.118812,72.4592144,17z/data=!4m14!1m7!3m6!1s0x395e9d0025f44ddd:0xf530ce00d3b58e7!2sDHARA+ESTATE!8m2!3d23.1188071!4d72.4617893!16s%2Fg%2f11vz5hvs6k!3m5!1s0x395e9dbd54bc0817:0x2c759f720692d1c5!8m2!3d23.1188042!4d72.4613264!16s%2Fg%2f11f0_g93sd?entry=ttu';
                      window.location.href = url;
                    }}
                    className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-semibold py-3 px-4 rounded-lg text-center transition-all shadow-md hover:shadow-lg cursor-pointer"
                  >
                    <MapPin className="inline-block mr-2" size={18} />
                    View on Google Maps
                  </button>
                </div>
              </div>
            </div>

            {/* Team Member Profile */}
            <div className="h-full">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-amber-200 h-full flex flex-col">
                {/* Profile Photo */}
                <div className="relative mb-6">
                  <div className="relative w-48 h-48 mx-auto rounded-full overflow-hidden border-4 border-amber-600 shadow-xl">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_desert-pump-guard/artifacts/n02yfgo3_Gemini_Generated_Image_enxbruenxbruenxb.png"
                      alt="Hasmukh Patel"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Experience Badge */}
                  <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-amber-600 to-orange-600 text-white px-6 py-2 rounded-full shadow-lg">
                    <div className="flex items-center space-x-2">
                      <Award size={18} />
                      <span className="font-bold text-sm">40+ Years Experience</span>
                    </div>
                  </div>
                </div>

                {/* Name & Title */}
                <div className="text-center mt-8 mb-6">
                  <h3 className="text-3xl font-bold text-gray-800 mb-2">Hasmukh Patel</h3>
                  <p className="text-lg text-amber-700 font-semibold mb-1">Founder & Owner</p>
                  <p className="text-sm text-gray-600">Leading Camel Rubber Bush since 2012</p>
                </div>

                {/* Bio */}
                <div className="space-y-4 mb-6 flex-grow">
                  <p className="text-gray-700 leading-relaxed text-center">
                    With over four decades of industry expertise, Hasmukh Patel stands as a visionary leader 
                    in rubber bush manufacturing. A true innovator, he has personally designed cutting-edge 
                    machinery that revolutionizes productivity and efficiency in the field.
                  </p>
                  <p className="text-gray-700 leading-relaxed text-center">
                    As a one-man army with unparalleled knowledge and experience, he brings extensive 
                    understanding of every aspect of the product. His unwavering commitment to quality, 
                    customer satisfaction, and unique problem-solving solutions has established Camel 
                    Rubber Industries as a trusted name in the industry.
                  </p>
                </div>

                {/* Key Highlights */}
                <div className="grid grid-cols-2 gap-4 pt-6 border-t border-amber-200">
                  <div className="bg-amber-50 p-4 rounded-xl text-center border border-amber-100">
                    <Award className="text-amber-600 mx-auto mb-2" size={28} />
                    <p className="font-bold text-gray-800">Quality Driven</p>
                    <p className="text-xs text-gray-600 mt-1">Premium Products</p>
                  </div>
                  <div className="bg-amber-50 p-4 rounded-xl text-center border border-amber-100">
                    <Lightbulb className="text-amber-600 mx-auto mb-2" size={28} />
                    <p className="font-bold text-gray-800">Innovation Expert</p>
                    <p className="text-xs text-gray-600 mt-1">Custom Solutions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
